import { Button, Modal } from '@evaneos/b2b-ui/legacy';
import { useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';

import { PictureAsPDF } from '../../../../../design-system/Icons/PictureAsPDF';
import { DEFERRED_TRIP_STEP } from '../../../../../domain';
import { DossierId, ViewVoucher } from '../../../../../resources/dossier/types';
import {
    useAddVoucherMutation,
    useDeleteVoucherMutation,
} from '../../../../../resources/dossier/mutations';
import { DeferModal } from '../DeferAgreement/DeferModal';

import { useGetDossierData } from '../../../../../resources/dossier/queries';
import { useLocale } from '../../../../../shared/providers/LocalizationProvider';
import { getEvaneosHostByLocale } from '../../../../../shared/intl/operations';

import s from './Vouchers.module.css';

const DELETE_VOUCHER = 'delete_voucher';
const UPLOAD_VOUCHER = 'upload_voucher';

export type ModalAllowed = typeof UPLOAD_VOUCHER | typeof DELETE_VOUCHER;

export const Vouchers = ({ dossierId }: { dossierId: DossierId }): JSX.Element => {
    const intl = useIntl();
    const addVoucherMutation = useAddVoucherMutation(dossierId);
    const deleteVoucherMutation = useDeleteVoucherMutation(dossierId);

    const { locale } = useLocale();
    const { dossier, contractVouchers } = useGetDossierData(dossierId) || {};

    const [isModalOpened, setIsModalOpened] = useState<ModalAllowed | false>(false);
    const [voucherToDelete, setVoucherToDelete] = useState<ViewVoucher | null>(null);

    const initialDate = new Date();

    const vouchers = useMemo(() => {
        return (contractVouchers || [])?.map((voucher) => ({
            id: voucher.id,
            url: `https://${getEvaneosHostByLocale(
                locale,
            )}/account/trip/${dossierId}/voucher/${voucher.id}`,
            filename: voucher.filename,
        }));
    }, [contractVouchers, dossierId, locale]);

    const handleModalUploadVoucher = (modal: ModalAllowed | false): void => {
        setIsModalOpened(modal);
    };

    const onDrop = (acceptedFiles: File[]): void => {
        addVoucherMutation.mutate({ file: acceptedFiles[0] });
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'application/pdf': ['.pdf'],
        },
        multiple: false,
        noDrag: true,
        useFsAccessApi: false,
        onDrop,
    });

    const handleModalDeleteVoucher = (modal: ModalAllowed | false, voucher: ViewVoucher | null) => {
        setIsModalOpened(modal);
        setVoucherToDelete(voucher);
    };

    const deleteVoucher = (selectedVoucher: ViewVoucher | null) => {
        if (selectedVoucher === null) {
            return;
        }
        deleteVoucherMutation.mutate({ voucherId: selectedVoucher.id });
        setIsModalOpened(false);
    };

    return (
        <div className={s.voucherContainer}>
            {(dossier?.step === DEFERRED_TRIP_STEP || vouchers.length > 0) && (
                <div className={s.titleContainer}>
                    <h3 className={s.title}>Vouchers</h3>
                </div>
            )}
            {dossier?.step === DEFERRED_TRIP_STEP && vouchers.length === 0 && (
                <Button
                    data-testid="upload-voucher-button"
                    priority="secondary"
                    onClick={() => handleModalUploadVoucher(UPLOAD_VOUCHER)}
                >
                    <FormattedMessage id="dossier.upload_voucher.add" />
                </Button>
            )}
            <ul>
                {vouchers.map((voucher) => (
                    <li key={voucher.id}>
                        <a href={voucher.url} target="_blank" rel="noreferrer noopener">
                            <PictureAsPDF color="var(--red)" />
                            <p className="annotation">{voucher.filename}</p>
                        </a>
                        <button
                            type="button"
                            className={s.deleteVoucher}
                            onClick={() => handleModalDeleteVoucher(DELETE_VOUCHER, voucher)}
                            data-testid="delete-voucher-button"
                        >
                            X
                        </button>
                    </li>
                ))}
            </ul>
            {dossier?.step === DEFERRED_TRIP_STEP && vouchers.length > 0 && (
                <div {...getRootProps()}>
                    <input id="voucher" {...getInputProps()} />
                    <Button priority="secondary" onClick={(e) => e.preventDefault()}>
                        <FormattedMessage id="dossier.upload_voucher.add" />
                    </Button>
                </div>
            )}
            {isModalOpened === UPLOAD_VOUCHER && (
                <DeferModal
                    isOpen={isModalOpened === UPLOAD_VOUCHER}
                    dossierId={dossierId}
                    handleModal={handleModalUploadVoucher}
                    onClose={() => handleModalUploadVoucher(false)}
                    initialDate={initialDate}
                />
            )}
            {isModalOpened === DELETE_VOUCHER && (
                <Modal
                    isOpen={isModalOpened === DELETE_VOUCHER}
                    title={intl.formatMessage({
                        id: 'dossier.delete_voucher.request',
                    })}
                    mainButton={
                        <Button priority="primary" onClick={() => deleteVoucher(voucherToDelete)}>
                            <FormattedMessage id="form.submit" />
                        </Button>
                    }
                    secondaryButton={
                        <Button
                            priority="secondary"
                            onClick={() => handleModalDeleteVoucher(false, voucherToDelete)}
                            data-testid="dossier-information-cancel-traveller-name"
                        >
                            <FormattedMessage id="form.cancel" />
                        </Button>
                    }
                >
                    <p>
                        <FormattedMessage id={`dossier.delete_voucher.message`} />
                    </p>
                </Modal>
            )}
        </div>
    );
};
